import { useCallback, useEffect, useRef, useState } from 'react'
import { useAutoSound } from '../../hooks/useAutoSound'
import { announceTileSound, onTileSoundClaimed, tileSoundToken } from '../../utils/tileSound'

export type LoadState = 'idle' | 'loading' | 'ready' | 'error'

// ── Громкость по умолчанию + персист последнего выбора ────────────
// Дефолт 100% — слишком громко (жалоба тестировщика). Стартуем с 50%,
// а выбранную пользователем громкость запоминаем в localStorage, чтобы
// она переживала перезагрузку страницы и применялась во всех плеерах.
const VOLUME_STORAGE_KEY = 'media-player-volume'
const DEFAULT_VOLUME = 0.5

function readStoredVolume(): number {
	try {
		const raw = localStorage.getItem(VOLUME_STORAGE_KEY)
		if (raw != null) {
			const v = parseFloat(raw)
			if (Number.isFinite(v) && v >= 0 && v <= 1) return v
		}
	} catch { /* private mode / disabled storage */ }
	return DEFAULT_VOLUME
}

function storeVolume(v: number) {
	try { localStorage.setItem(VOLUME_STORAGE_KEY, String(v)) } catch { /* ignore */ }
}

// Перцептивная кривая громкости: <audio>.volume — линейная АМПЛИТУДА, а ухо слышит
// логарифмически → без кривой низ ползунка (~0-30%) даёт неслышимую амплитуду (баг
// «звук пропадает на ~30%»). posToAmp поднимает низ (k<1), ampToPos — обратно для слайдера.
const VOLUME_CURVE = 0.6
const posToAmp = (pos: number): number => (pos <= 0 ? 0 : Math.pow(pos, VOLUME_CURVE))
const ampToPos = (amp: number): number => (amp <= 0 ? 0 : Math.pow(amp, 1 / VOLUME_CURVE))

export interface MediaPlayerState {
	isPlaying: boolean
	duration: number
	currentTime: number
	volume: number
	muted: boolean
	loadState: LoadState
}

export interface MediaPlayerControls {
	togglePlay: () => void
	play: () => void
	pause: () => void
	seek: (time: number) => void
	seekRatio: (ratio: number) => void
	setVolume: (v: number) => void
	toggleMute: () => void
	retry: () => void
}

interface UseMediaPlayerOptions {
	/** Начальное время воспроизведения (для resume) — применяется однократно после loadedmetadata */
	initialTime?: number
	/** Запустить play() автоматически после applyInitialTime. Детерминированнее
	 *  чем autoplay attribute на самом элементе (нет race с browser autoplay). */
	autoPlay?: boolean
	/** Стартовать без звука. Для авто-плеящихся результатов генерации:
	 *  неожиданный звук на всю громкость пугает, а браузерная autoplay-policy
	 *  может вовсе заблокировать незаглушённый запуск. Юзер включает звук
	 *  кнопкой громкости — применится сохранённый уровень.
	 *
	 *  Это ПРОСЬБА компонента, а не приказ: если юзер включил в ЛК
	 *  «автоматически включать звук» (users.auto_sound), просьба
	 *  игнорируется и старт идёт со звуком — с фолбэком ниже на случай,
	 *  когда браузер такой автозапуск запретит. */
	startMuted?: boolean
	/** Один звучащий источник на страницу (utils/tileSound): зазвучавший плеер
	 *  объявляет звук — плитки витрин и соседние плееры глохнут; чужая заявка
	 *  глушит этот плеер (mute без паузы). MediaPlayer включает для video. */
	sharedSound?: boolean
}

/**
 * Общий хук для <audio>/<video>. Привязывает listeners к media-элементу через ref.
 * Возвращает state + controls. Не зависит от вариантов audio/video — универсальный.
 */
export function useMediaPlayer(
	mediaRef: React.RefObject<HTMLMediaElement | null>,
	src: string,
	options?: UseMediaPlayerOptions,
): MediaPlayerState & MediaPlayerControls {
	const [isPlaying, setIsPlaying] = useState(false)
	const [duration, setDuration] = useState(0)
	const [currentTime, setCurrentTime] = useState(0)
	const [volume, setVolumeState] = useState(() => ampToPos(readStoredVolume()))
	const [muted, setMuted] = useState(false)
	const [loadState, setLoadState] = useState<LoadState>('idle')
	const [retryKey, setRetryKey] = useState(0)
	// Общий звук страницы: метка этого плеера и «звучит ли он сейчас» — звук
	// объявляем только на переходе «молчал → зазвучал», а не на каждом событии.
	const [soundToken] = useState(tileSoundToken)
	const soundingRef = useRef(false)
	const initialTimeAppliedRef = useRef(false)
	const autoPlayTriggeredRef = useRef(false)
	// Храним options в ref чтобы не пересоздавать listeners при изменении
	const optionsRef = useRef(options)
	useEffect(() => { optionsRef.current = options }, [options])

	// Настройка аккаунта «автоматически включать звук». В ref по той же
	// причине, что и options: listeners навешиваются один раз, а значение
	// может поменяться (тумблер в ЛК, соседняя вкладка).
	const autoSound = useAutoSound()
	const autoSoundRef = useRef(autoSound)
	useEffect(() => { autoSoundRef.current = autoSound }, [autoSound])
	// true, пока mute стоит ПО НАШЕЙ инициативе (startMuted + выключенная
	// настройка). Юзер тронул звук — сбрасываем: его выбор приоритетнее.
	const autoMutedRef = useRef(false)

	// Пересброс state при смене src или retry
	useEffect(() => {
		setIsPlaying(false)
		setDuration(0)
		setCurrentTime(0)
		setLoadState(src ? 'loading' : 'idle')
		initialTimeAppliedRef.current = false
		autoPlayTriggeredRef.current = false
		soundingRef.current = false
	}, [src, retryKey])

	// Слушатели media-элемента
	useEffect(() => {
		const el = mediaRef.current
		if (!el || !src) return

		const applyInitialTime = () => {
			if (initialTimeAppliedRef.current) return
			const t = optionsRef.current?.initialTime
			if (typeof t !== 'number' || t <= 0) {
				initialTimeAppliedRef.current = true
				return
			}
			const max = Number.isFinite(el.duration) && el.duration > 0 ? el.duration : t
			el.currentTime = Math.min(t, max)
			initialTimeAppliedRef.current = true
		}

		const triggerAutoPlayIfNeeded = () => {
			if (autoPlayTriggeredRef.current) return
			if (!optionsRef.current?.autoPlay) return
			autoPlayTriggeredRef.current = true
			void el.play().catch(() => {
				// Autoplay-policy пропускает незаглушённый старт только после
				// пользовательского жеста на странице. Без этого фолбэка при
				// включённом авто-звуке видео просто не поехало бы (заметнее
				// всего на iOS Safari). Играем беззвучно — юзер снимет mute.
				if (el.muted) return
				el.muted = true
				setMuted(true)
				void el.play().catch(() => {})
			})
		}

		// Зазвучал (играет, не заглушён, громкость > 0) — объявляем: остальные
		// источники на странице глохнут. Свою же заявку плеер игнорирует.
		const syncSounding = () => {
			const now = !el.paused && !el.muted && el.volume > 0
			if (now && !soundingRef.current && optionsRef.current?.sharedSound) announceTileSound(soundToken)
			soundingRef.current = now
		}

		const onLoadedMeta = () => {
			if (Number.isFinite(el.duration)) setDuration(el.duration)
			setLoadState('ready')
			applyInitialTime()
			triggerAutoPlayIfNeeded()
		}
		const onTime = () => setCurrentTime(el.currentTime)
		const onPlay = () => {
			setIsPlaying(true)
			syncSounding()
		}
		const onPause = () => {
			setIsPlaying(false)
			syncSounding()
		}
		const onEnded = () => {
			setIsPlaying(false)
			syncSounding()
		}
		const onVolume = () => {
			setVolumeState(ampToPos(el.volume))
			setMuted(el.muted)
			syncSounding()
		}
		const onError = () => setLoadState('error')
		// Намеренно НЕ слушаем 'waiting' — во время seek медиа-элемент
		// кратко уходит в waiting state, это дёргает loadState → дизейблит
		// play-кнопку. UX: во время seek плеер должен оставаться активным.

		el.addEventListener('loadedmetadata', onLoadedMeta)
		el.addEventListener('durationchange', onLoadedMeta)
		el.addEventListener('timeupdate', onTime)
		el.addEventListener('play', onPlay)
		el.addEventListener('pause', onPause)
		el.addEventListener('ended', onEnded)
		el.addEventListener('volumechange', onVolume)
		el.addEventListener('error', onError)

		// Инициализация state из текущего состояния элемента
		// startMuted — ставим до любого play(): autoplay-policy пропускает
		// только заглушённый автозапуск, и юзер не получает внезапный звук.
		// Настройка аккаунта «автоматически включать звук» эту просьбу
		// отменяет (см. utils/autoSound.ts).
		// Новый элемент/src — флаг считаем заново, чтобы состояние прошлого
		// медиа («мы заглушили» / «юзер трогал») сюда не протекало
		autoMutedRef.current = false
		if (optionsRef.current?.startMuted && !autoSoundRef.current) {
			el.muted = true
			autoMutedRef.current = true
		}

		if (el.readyState >= 1 && Number.isFinite(el.duration)) {
			setDuration(el.duration)
			setLoadState('ready')
			applyInitialTime()
			triggerAutoPlayIfNeeded()
		}
		// Применяем сохранённую/дефолтную громкость к элементу (нативный
		// дефолт — 1.0, т.е. 100%). volumechange-listener синхронизирует state.
		el.volume = readStoredVolume()
		setVolumeState(ampToPos(el.volume))
		setMuted(el.muted)

		return () => {
			el.removeEventListener('loadedmetadata', onLoadedMeta)
			el.removeEventListener('durationchange', onLoadedMeta)
			el.removeEventListener('timeupdate', onTime)
			el.removeEventListener('play', onPlay)
			el.removeEventListener('pause', onPause)
			el.removeEventListener('ended', onEnded)
			el.removeEventListener('volumechange', onVolume)
			el.removeEventListener('error', onError)
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [src, retryKey])

	const play = useCallback(() => {
		const el = mediaRef.current
		if (!el) return
		// Ошибки play() (NotAllowedError по autoplay policy и т.п.) игнорируем —
		// настоящие ошибки загрузки ловим через 'error' event listener.
		void el.play().catch(() => {})
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [])

	const pause = useCallback(() => {
		mediaRef.current?.pause()
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [])

	const togglePlay = useCallback(() => {
		const el = mediaRef.current
		if (!el) return
		if (el.paused) void el.play().catch(() => {})
		else el.pause()
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [])

	const seek = useCallback((time: number) => {
		const el = mediaRef.current
		if (!el || !Number.isFinite(time)) return
		el.currentTime = Math.max(0, Math.min(time, el.duration || time))
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [])

	const seekRatio = useCallback((ratio: number) => {
		const el = mediaRef.current
		if (!el || !el.duration) return
		el.currentTime = Math.max(0, Math.min(1, ratio)) * el.duration
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [])

	const setVolume = useCallback((v: number) => {
		const el = mediaRef.current
		if (!el) return
		const clamped = Math.max(0, Math.min(1, v))
		el.volume = posToAmp(clamped)
		storeVolume(el.volume)
		if (clamped > 0 && el.muted) el.muted = false
		autoMutedRef.current = false
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [])

	const toggleMute = useCallback(() => {
		const el = mediaRef.current
		if (!el) return
		el.muted = !el.muted
		autoMutedRef.current = false
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [])

	// Юзер включил настройку (в ЛК или соседней вкладке) — снимаем ТОЛЬКО
	// наш автоматический mute у уже играющего медиа. Обратное не делаем:
	// звук, включённый руками, сам выключаться не должен.
	useEffect(() => {
		if (!autoSound) return
		const el = mediaRef.current
		if (el && autoMutedRef.current && el.muted) {
			el.muted = false
			autoMutedRef.current = false
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [autoSound])

	// Звук забрал другой источник (плитка витрины, соседний плеер, лайтбокс):
	// глохнем, не останавливаясь — кнопка звука вернёт его и заберёт обратно.
	useEffect(() => {
		if (!options?.sharedSound) return
		return onTileSoundClaimed(soundToken, () => {
			const el = mediaRef.current
			if (!el || el.muted) return
			el.muted = true
			autoMutedRef.current = false
		})
	}, [options?.sharedSound, soundToken, mediaRef])

	const retry = useCallback(() => {
		setRetryKey((k) => k + 1)
		setLoadState('loading')
		const el = mediaRef.current
		if (el) {
			try { el.load() } catch { /* ignore load errors — ошибка поймана через error event */ }
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [])

	return {
		isPlaying,
		duration,
		currentTime,
		volume,
		muted,
		loadState,
		togglePlay,
		play,
		pause,
		seek,
		seekRatio,
		setVolume,
		toggleMute,
		retry,
	}
}

// ── Утилита форматирования времени mm:ss ──────────────────────────
export function formatTime(seconds: number): string {
	if (!Number.isFinite(seconds) || seconds < 0) return '0:00'
	const total = Math.floor(seconds)
	const m = Math.floor(total / 60)
	const s = total % 60
	return `${m}:${s < 10 ? '0' : ''}${s}`
}
